//@ts-check
/** @typedef {import('./types/organisationOverview.types.js').OrgOverviewResponse} OrgOverviewResponse */
import axios from 'axios';

import { useAuthenticatedQuery } from './useAuthenticatedQuery';
import { useOrgContext } from '@datahooks/admin/organisation/AdminOrgIDContext.jsx';
import useUser from '@datahooks/useUser';

import { API_BASE_URL, FUNCTION_KEY, PROJECT_SCOPE } from '../config';
import logError from './metadata/ErrorLogging';

/**
 * Fetches organisation-level overview (matrix and KPIs).
 *
 * @function useOrganisationOverview
 *
 * @returns {{
 *   orgOverview: OrgOverviewResponse,
 *   isLoading: boolean,
 *   refetch: () => Promise<any>,
 *   error: any
 * }}
 */
export default function useOrganisationOverview() {
  const { organisationData } = useOrgContext();

  const fetchOrganisationOverview = async (token) => {
    const requestParams = organisationData?.id ? { org_id: organisationData.id } : {};

    try {
      const response = await axios.get(`${API_BASE_URL}organisation_overview`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-functions-key': FUNCTION_KEY,
          'Content-Type': 'application/json',
        },
        params: requestParams,
      });

      const responseData = response.data;

      return responseData;
    } catch (error) {
      logError('Failed to load organisation overview in useOrganisationOverview', error, requestParams);
      throw error;
    }
  };

  const projectScope = [PROJECT_SCOPE];
  const { user } = useUser();
  const userId = user?.id ?? null;

  // Create a query key that includes all the parameters that could affect the result
  const queryKey = ['organisationOverview', organisationData?.id, userId];

  const {
    data: orgOverview = {},
    isLoading,
    refetch,
    error,
  } = useAuthenticatedQuery(queryKey, fetchOrganisationOverview, projectScope, {
    enabled: !!organisationData?.id,
    staleTime: 60 * 1000, // Data will be cached for 1 minute
  });

  return { orgOverview, isLoading, refetch, error };
}
